<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/typography";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .email-limits {
    display: flex;
    flex-direction: column;
    padding: utils.in_rem(24px) utils.in_rem(20px);
    gap: utils.in_rem(24px);

    h3 {
      color: colors.get("primary_50");
    }
    p {
      @include mixins.body2();
    }

    .input-row {
      display: flex;
      gap: 4px;
      width: 100%;
      align-items: center;
    }
    .button-row {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }
</style>

<script>
  import { onDestroy } from "svelte";
  import { derived } from "svelte/store";
  import { teamStore } from "../../../../api-stores/license-store";
  import { adminModify } from "../../../../api/admin";
  import { StagingStore } from "../../../../stores/staging-store";

  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import IconButton from "@sourcewhale/shared/svelte-design-system/components/atoms/IconButton.svelte";
  import TextToggle from "@sourcewhale/shared/svelte-design-system/components/atoms/Toggles/TextToggle.svelte";
  import SliderInputLabel from "@sourcewhale/shared/svelte-design-system/components/molecules/SimpleInputs/SliderInputLabel.svelte";
  teamStore.once();

  const defaults = {
    minGapLocked: false,
    sendLimitLocked: false,
    sendLimit: 150,
    minGap: 180,
  };
  const remoteStore = derived(teamStore, ($teamStore) => ({
    ...defaults,
    ...$teamStore.emailSendSettings,
    enabled: Object.keys($teamStore.emailSendSettings || {}).length > 0,
  }));

  const localStore = new StagingStore({
    store: remoteStore,
    initialValue: {
      enabled: false,
      ...defaults,
    },
  });
  const { dirty } = localStore;

  onDestroy(() => {
    localStore.destroy();
  });

  const toggleMinGapLocked = () => {
    $localStore.minGapLocked = !$localStore.minGapLocked;
  };

  const toggleSendLimitLocked = () => {
    $localStore.sendLimitLocked = !$localStore.sendLimitLocked;
  };

  // Second part handles that we don't care if the local settings have
  // changed relative to remote if both local and remote are not enabled.
  let disabled = $derived(
    !($dirty && ($remoteStore.enabled || $localStore.enabled))
  );

  const saveEmailSendLimit = async () => {
    const { enabled, ...config } = $localStore;
    const emailSendSettings = enabled ? config : {};
    await adminModify({
      kind: "team",
      data: { emailSendSettings },
    });
    await teamStore.get();
  };
</script>

<div class="email-limits">
  <div>
    <h3>Daily Sending Limit</h3>
    <p>
      Set the maximum emails your team can send each day and the minimum time
      between emails. Lock these settings to prevent your team from making
      changes.
    </p>
  </div>
  <TextToggle label="Daily Sending Limit" bind:checked={$localStore.enabled} />
  {#if $localStore.enabled}
    <div class="input-row">
      <SliderInputLabel
        class="flex-1"
        style="max-width: 600px"
        min="5"
        max="300"
        step="5"
        bind:value={$localStore.minGap}
        label="Minimum time between emails"
      />
      <IconButton
        style="margin-top: 16px"
        icon={$localStore.minGapLocked ? "fas fa-lock" : "fas fa-lock-open"}
        onclick={() => toggleMinGapLocked()}
      >
        <span slot="tooltip">
          Once locked, your team cannot adjust these settings
        </span>
      </IconButton>
    </div>

    <div class="input-row">
      <SliderInputLabel
        class="flex-1"
        style="max-width: 600px"
        min="0"
        max="1000"
        step="5"
        bind:value={$localStore.sendLimit}
        label="User daily sending limit"
      />
      <IconButton
        style="margin-top: 16px"
        icon={$localStore.sendLimitLocked ? "fas fa-lock" : "fas fa-lock-open"}
        onclick={() => toggleSendLimitLocked()}
      >
        <span slot="tooltip">
          Once locked, your team cannot adjust these settings
        </span>
      </IconButton>
    </div>
  {/if}
  <div class="button-row">
    <Button
      leadingIcon="fa-regular fa-save"
      kind={disabled ? "disabled" : "tertiary-blue"}
      onclick={saveEmailSendLimit}
      {disabled}
    >
      Save
    </Button>
  </div>
</div>
